import React, { useEffect } from 'react';

/**
 * ConsoleErrorHandler Component
 *
 * Purpose: This component wraps the entire application and filters console errors/warnings
 * to reduce noise from non-critical issues while preserving important error information.
 *
 * What it does:
 * - Filters out chunk loading errors (handled by React's built-in fallbacks)
 * - Filters out React DevTools warnings in development
 * - Filters out React strict mode warnings that don't affect functionality
 * - Allows all legitimate API errors, authentication errors, and application errors to be displayed
 *
 * Why it's needed:
 * - Prevents console spam from non-critical errors that can confuse developers
 * - Maintains visibility of important errors for debugging
 * - Improves developer experience by focusing on actionable errors
 *
 * Note: This component does NOT suppress legitimate API errors or application errors.
 * All authentication, authorization, and server errors are properly logged.
 */

interface ConsoleErrorHandlerProps {
  children: React.ReactNode;
}

const ConsoleErrorHandler: React.FC<ConsoleErrorHandlerProps> = ({ children }) => {
  useEffect(() => {
    // Store original console methods
    const originalError = console.error;
    const originalWarn = console.warn;

    // Override console.error to catch and handle errors
    console.error = (...args: any[]) => {
      // Filter out known non-critical errors
      const message = args.join(' ');

      // Skip chunk loading errors that are handled by fallbacks
      if (message.includes('Loading chunk') ||
          message.includes('ChunkLoadError') ||
          message.includes('Loading CSS chunk')) {
        console.log('🔄 Chunk loading issue (handled by fallback):', message);
        return;
      }

      // Skip React DevTools warnings in development
      if (message.includes('React DevTools') ||
          message.includes('Download the React DevTools')) {
        return;
      }

      // Skip React strict mode warnings that appear as errors
      if (message.includes('Warning: ReactDOM.render is deprecated') ||
          message.includes('Warning: componentWillMount has been renamed') ||
          message.includes('Warning: componentWillReceiveProps has been renamed')) {
        return;
      }

      // Allow all other errors to be logged normally, including API errors
      // This ensures that legitimate errors (like 404, 500, authentication issues) are visible
      originalError.apply(console, args);
    };

    // Override console.warn to filter warnings
    console.warn = (...args: any[]) => {
      const message = args.join(' ');

      // Skip React strict mode warnings
      if (message.includes('StrictMode') ||
          message.includes('findDOMNode')) {
        return;
      }

      // Skip chunk size warnings
      if (message.includes('chunk size limit') ||
          message.includes('chunkSizeWarningLimit')) {
        return;
      }

      // Skip React DevTools warnings
      if (message.includes('React DevTools') ||
          message.includes('Download the React DevTools')) {
        return;
      }

      // Log other warnings normally
      originalWarn.apply(console, args);
    };

    // Global error handler for unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;

      // Only suppress chunk loading and non-critical network errors
      if (reason && typeof reason === 'object' && reason.message) {
        const message = reason.message.toString();
        if (message.includes('Loading chunk') ||
            message.includes('ChunkLoadError')) {
          console.log('🔄 Unhandled chunk loading rejection (handled):', reason);
          event.preventDefault();
          return;
        }
      }

      // Log all other unhandled rejections normally for debugging
      console.error('🚨 Unhandled promise rejection:', reason);
      // Don't prevent default behavior for legitimate errors
    };

    // Global error handler for JavaScript errors
    const handleError = (event: ErrorEvent) => {
      const { message, filename, lineno, colno, error } = event;

      // Skip chunk loading errors
      if (message.includes('Loading chunk') ||
          message.includes('ChunkLoadError')) {
        console.log('🔄 Script loading error (handled):', message);
        event.preventDefault();
        return;
      }

      // Log all other JavaScript errors normally
      console.error('🚨 Global JavaScript error:', {
        message,
        filename,
        lineno,
        colno,
        error
      });
    };

    // Add global error listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup function
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, []);

  return <>{children}</>;
};

export default ConsoleErrorHandler;
